////////////
// <tmv2-stream-target>

// Defines a placeholder element used by PseudoStreamUtils#render_streaming.
//
// Usage (Ruby):
//   PseudoStreamUtils#render_streaming(..., segments: {
//     "source-key" => -> { "text-to-add" }
//   })
//
// Corresponding HTML output:
//   <tmv2-stream-target source="source-key"></tmv2-stream-target>
//
// How it works:
// - During render_streaming, the page is sent to the client immediately.
// - A script injected at the end of the page later finds these elements
//   and streams content into them.
// - This enables progressive rendering of additional information,
//   while remaining fully driven by Ruby code.
//
// IMPORTANT CONSTRAINTS (very strict):
// - The element MUST exactly match the syntax above.
// - The attribute name must be `source`.
// - The attribute value must use double quotes.
// - No additional attributes are allowed.
// - Detection is done via a naive string search, not an HTML parser.
// - The element itself can have contents,
//   these are replaced once the final contents have been sent.
//   This can be used for placeholders.
//
// Behavior:
// - This custom element is inert by itself.
// - If the streamed string is HTML-safe, content is inserted via `innerHTML`;
//   otherwise `innerText` is used.
// - The element uses `display: contents`, so injected nodes (within its css) behave as if they
//   were rendered directly into the parent element (no extra wrapper).
window.customElements.define("tmv2-stream-target", class StreamTarget extends HTMLElement {});


////////////
// <tmv2-async-fetch>

const __requestList = new Map<string, Promise<{[name: string]: ({html: boolean, contents: string})}>>;
function requestAsyncFetch(name: string) {
    if (!__requestList.has(name)) {
        __requestList.set(name, (async () => {
            const response = await fetch(window.location.href, {
                method: "GET",
                credentials: "same-origin",
                mode: "same-origin",
                headers: {
                    "Accept": "application/json",
                    "X-Message-Key": name,
                },
            });
            return (await response.json()) as {[name: string]: {html: boolean, contents: string}};
        })());
    }
    return __requestList.get(name)!;
}

window.customElements.define("tmv2-async-fetch", class AsyncFetch extends HTMLElement {
    static observedAttributes = ["key", "segment"];

    #connected = false;
    #epoch = 0;

    __reload() {
        const selfEpoch = (++this.#epoch);
        if (!this.#connected) return;

        const currentKey = this.getAttribute("key") || "";
        const currentSegment = this.getAttribute("segment") || "default";
        (async () => {
            const json = await requestAsyncFetch(currentKey);
            if (this.#epoch !== selfEpoch) return;
            if (!json[currentSegment]) return;
            if (json[currentSegment].html) {
                this.innerHTML = json[currentSegment].contents;
            } else {
                this.innerText = json[currentSegment].contents;
            }
        })();
    }

    connectedCallback() {
        this.#connected = true;
        this.__reload();
    }

    disconnectCallback() {
        this.#connected = false;
        this.__reload();
    }
    attributeChangedCallback() {
        this.__reload();
    }
});

////////////
// <tmv2-island>


type Runner = (element: Island, settings: any) => /* unmap */ (() => void);
const _runners = new Map<string, Runner>;
class Island extends HTMLElement {
   static observedAttributes = ["runner", "config"];

   #unload: () => void = () => {};
   #connected = false;

   private __unload() {
       this.#unload();
       this.#unload = () => {};
   }

   __reload() {
       if (!this.#connected) return;

       this.__unload();
       const newRunner = _runners.get(this.getAttribute("runner") || "_does_not_exist") || (() => (() => {}));
       this.#unload = newRunner(this, JSON.parse(this.getAttribute("config") || "null"));
   }
   
   connectedCallback() {
       this.__unload();
       this.#connected = true;
       this.__reload();
   }

    disconnectCallback() {
        this.__unload();
        this.#connected = false;
    }

    attributeChangedCallback() {
        this.__reload();
    }
}
window.customElements.define("tmv2-island", Island);


// Islands provide explicit boundaries for client-side reactivity.
// Only code inside an island is reactive; the rest of the page remains static.
//
// An island is declared in HTML as:
//   <tmv2-island
//     runner="name"
//     config='{"any":"json","possibly":"server-generated"}'>
//   </tmv2-island>
//
// The `runner` attribute selects a registered island runner by name.
// The `config` attribute is parsed as JSON and passed to the runner.
//
// Runner lifecycle:
// - A runner is a function with the signature:
//     (island: Island, config: ParsedJSON) => () => void
//
// - The runner is called when the island is mounted.
// - It may attach listeners, create reactive state, or render content.
// - The function it returns is an unmount callback, which is invoked when
//   the island is removed or reloaded.
//
// Reactivity control:
// - Islands are the *only* place where reactivity is allowed.
// - Everything outside an island is non-reactive and static.
//
// Reload behavior:
// - Changing either the `runner` or `config` attribute causes:
//     1. The current island to be unmounted (unmount callback is called)
//     2. The island to be mounted again with the new attributes
//
// Registration behavior:
// - Calling `registerIsland` registers (or replaces) a runner by name.
// - Any existing <tmv2-island> elements using that runner name are
//   immediately reloaded.
export function registerIsland(name: string, runner: Runner) {
    _runners.set(name, runner);
    for (let target of document.querySelectorAll(`tmv2-island[runner="${name}"]`)) {
        (target as Island).__reload();
    }
}

export function registerIslandModule(name: string, runner: () => Promise<{default: Runner}>) {
    registerIsland(name, (island, config) => {
        let connected = true;
        let disconnect = () => {connected = false};
        runner().then(({default: def}) => {
            if (!connected) return;
            disconnect = def(island, config);
        }).catch(console.error);
        return () => disconnect();
    });
}
